import {
  selectModeIsLoadingData,
  selectModeIsTransitioning,
  selectModeName,
} from "@/store/mode-selectors";
import type { AppThunk, RootState } from "@/store/store";
import { assert } from "@faro-lotv/foundation";
import { isPlainObject } from "es-toolkit";
import { activeSheet } from "./active-sheet";
import { elementAndMode, isViewingMode } from "./element-and-mode";
import { splitCompare } from "./split-compare";
import { TrackedState } from "./tracked-state";

/**
 * The parts of the viewer state the navigation history records and restores, in
 * restore order. To track more state, add a `TrackedState` file and list it here.
 * A part that builds on another one declares it in `restoreAfter`.
 */
const TRACKED_STATES = [elementAndMode, activeSheet, splitCompare] as const;

/**
 * @param parts The tracked states in restore order
 * @throws if a part is listed before a part it is restored after
 */
export function assertRestoreOrder(
  parts: ReadonlyArray<TrackedState<object>>,
): void {
  parts.forEach(({ restoreAfter = [] }, index) => {
    for (const dependency of restoreAfter) {
      const dependencyIndex = parts.indexOf(dependency);
      assert(
        dependencyIndex >= 0 && dependencyIndex < index,
        `Tracked state ${index} is listed before a part in its restoreAfter`,
      );
    }
  });
}

assertRestoreOrder(TRACKED_STATES);

/**
 * Turns a union into an intersection, e.g. `A | B` into `A & B`, so the entry
 * types of all tracked states merge into one entry. Works by placing the union in
 * a function parameter, where inferring it back yields the intersection.
 */
type UnionToIntersection<U> = (
  U extends unknown ? (union: U) => void : never
) extends (intersection: infer I) => void
  ? I
  : never;

/** The viewer state a browser history entry restores, as a subset of a saved view */
export type NavigationHistoryEntry = UnionToIntersection<
  (typeof TRACKED_STATES)[number] extends TrackedState<infer Entry>
    ? Entry
    : never
>;

/**
 * The tracked states, reading their own share of the whole entry and each
 * capturing only their part of it
 */
const trackedStates: ReadonlyArray<
  Omit<TrackedState<NavigationHistoryEntry>, "capture"> & {
    capture(state: RootState): object | undefined;
  }
> = TRACKED_STATES;

/**
 * @param value The value to check, e.g. read from the browser history
 * @returns whether the value is a navigation history entry
 */
export function isNavigationHistoryEntry(
  value: unknown,
): value is NavigationHistoryEntry {
  return (
    isPlainObject(value) && trackedStates.every(({ isEntry }) => isEntry(value))
  );
}

/**
 * @param state The current state
 * @returns the navigation state to record, or undefined while it should not be
 * recorded (loading, transitioning or running an exclusive workflow)
 */
export function selectNavigationHistoryEntry(
  state: RootState,
): NavigationHistoryEntry | undefined {
  if (
    !isViewingMode(selectModeName(state)) ||
    selectModeIsTransitioning(state) ||
    selectModeIsLoadingData(state)
  ) {
    return;
  }
  const entry = {};
  for (const { capture } of trackedStates) {
    const part = capture(state);
    if (!part) return;
    Object.assign(entry, part);
  }
  return isNavigationHistoryEntry(entry) ? entry : undefined;
}

/**
 * Restores a recorded navigation state. Unlike `applySavedView` it leaves the
 * tool, clipping box and filters untouched. Nothing is restored outside of the
 * viewing modes, or if any part of the entry is no longer valid for the project.
 *
 * @param entry The state to restore
 * @returns a thunk restoring the state, returning whether it did
 */
export function applyNavigationHistoryEntry(
  entry: NavigationHistoryEntry,
): AppThunk<boolean> {
  return (dispatch, getState) => {
    const state = getState();
    if (
      !isViewingMode(selectModeName(state)) ||
      !trackedStates.every(({ canRestore }) => canRestore(entry, state))
    ) {
      return false;
    }
    for (const { restore } of trackedStates) dispatch(restore(entry));
    return true;
  };
}
